<script lang="ts" setup>
import PriceTypeFilter from '@/views/search/PriceTypeFilter.vue';
import MobileFilters from '@/views/search/MobileFilters.vue';
import { Disclosure, DisclosureButton, DisclosurePanel, Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import CategoryFilter from '@/views/search/CategoryFilter.vue';
import DesktopFilters from '@/views/search/DesktopFilters.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { AdjustmentsHorizontalIcon } from '@heroicons/vue/24/outline';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBath, faBed, faCircleDollar, faHouseBlank } from '@fortawesome/pro-regular-svg-icons';
import PriceFilter from '@/views/search/PriceFilter.vue';
import BedroomsFilter from '@/views/search/BedroomsFilter.vue';
import BathroomsFilter from '@/views/search/BathroomsFilter.vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

<template>
  <div>
    <!-- Desktop Filters -->
    <DesktopFilters>
      <!-- Desktop Price Type Buttons -->
      <div class="xs-sm:block hidden border-r border-gray-200">
        <PriceTypeFilter class="pr-3" />
      </div>
      <!-- Desktop Category Dropdown -->
      <div class="hidden border-r border-gray-200 md:block">
        <Popover class="relative px-2">
          <PopoverButton class="focus:outline-ph-green-600 flex cursor-pointer items-center rounded-md p-2 text-base/8 font-medium focus:outline-2">
            <FontAwesomeIcon :icon="faHouseBlank" class="2xs:size-6! size-5!" size="2xl" />
            <span class="pl-2 text-gray-700">{{ t('views.search.categories') }}</span>
            <ChevronDownIcon aria-hidden="true" class="-mr-1 ml-1 h-5 w-5 shrink-0 text-gray-400" />
          </PopoverButton>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel class="absolute right-1.5 z-100 mt-2 origin-top-right columns-2 space-y-4 rounded-md bg-white p-4 shadow-2xl ring-1 ring-gray-500/50 focus:outline-hidden">
              <CategoryFilter />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
      <!-- Desktop Price Dropdown -->
      <div class="md-lg:block hidden border-r border-gray-200">
        <Popover class="relative px-2">
          <PopoverButton class="focus:outline-ph-green-600 flex cursor-pointer items-center rounded-md p-2 text-base/8 font-medium focus:outline-2">
            <FontAwesomeIcon :icon="faCircleDollar" class="2xs:size-6! size-5!" size="2xl" />
            <span class="pl-2">{{ t('views.search.price') }}</span>
            <ChevronDownIcon aria-hidden="true" class="-mr-1 ml-1 h-5 w-5 shrink-0 text-gray-400" />
          </PopoverButton>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel class="absolute right-1.5 z-100 mt-2 origin-top-right flex-col justify-items-center space-y-4 rounded-md bg-white p-4 shadow-2xl ring-1 ring-gray-500/50 focus:outline-hidden">
              <PriceFilter />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
      <!-- Desktop Bedrooms Dropdown -->
      <div class="lg-lg-xl:block hidden border-r border-gray-200">
        <Popover class="relative px-2">
          <PopoverButton class="focus:outline-ph-green-600 flex cursor-pointer items-center rounded-md p-2 text-base/8 font-medium focus:outline-2">
            <FontAwesomeIcon :icon="faBed" class="2xs:size-6! size-5!" size="2xl" />
            <span class="pl-2">{{ t('views.search.bedrooms') }}</span>
            <ChevronDownIcon aria-hidden="true" class="-mr-1 ml-1 h-5 w-5 shrink-0 text-gray-400" />
          </PopoverButton>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel class="absolute right-1.5 z-100 mt-2 origin-top-right flex-col justify-items-center space-y-4 rounded-md bg-white p-4 shadow-2xl ring-1 ring-gray-500/50 focus:outline-hidden">
              <BedroomsFilter />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
      <!-- Desktop Bathrooms Dropdown -->
      <div class="lg-xl:block hidden">
        <Popover class="relative pr-0.5 pl-2">
          <PopoverButton class="focus:outline-ph-green-600 flex cursor-pointer items-center rounded-md p-2 text-base/8 font-medium focus:outline-2">
            <FontAwesomeIcon :icon="faBath" class="2xs:size-6! size-5!" size="2xl" />
            <span class="pl-2">{{ t('views.search.bathrooms') }}</span>
            <ChevronDownIcon aria-hidden="true" class="-mr-1 ml-1 h-5 w-5 shrink-0 text-gray-400" />
          </PopoverButton>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel class="absolute right-1.5 z-100 mt-2 origin-top-right flex-col justify-items-center space-y-4 rounded-md bg-white p-4 shadow-2xl ring-1 ring-gray-500/50 focus:outline-hidden">
              <BathroomsFilter />
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
      <!-- Desktop Filters Dropdown -->
      <div class="lg-xl:hidden hidden md:block">
        <Popover class="relative pl-2">
          <PopoverButton class="focus:outline-ph-green-600 flex cursor-pointer items-center rounded-md p-2 text-base/8 font-medium focus:outline-2">
            <AdjustmentsHorizontalIcon class="2xs:h-7 2xs:w-7 h-6 w-6" />
            <span class="pl-1.5">{{ t('views.search.filters') }}</span>
            <ChevronDownIcon aria-hidden="true" class="-mr-1 ml-1 h-5 w-5 shrink-0 text-gray-400" />
          </PopoverButton>
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <PopoverPanel class="absolute right-1.5 z-100 mt-2 origin-top-right flex-col justify-items-center space-y-4 rounded-md bg-white p-4 shadow-2xl ring-1 ring-gray-500/50 focus:outline-hidden">
              <div class="space-y-4 divide-y divide-gray-200">
                <PriceFilter class="md-lg:hidden block pb-4" />
                <BedroomsFilter class="lg-lg-xl:hidden block pb-4" />
                <BathroomsFilter />
              </div>
            </PopoverPanel>
          </Transition>
        </Popover>
      </div>
      <!-- Mobile Filters -->
      <MobileFilters class="md:hidden">
        <!-- Mobile Price Type Filter -->
        <div class="xs-sm:hidden block pb-1">
          <div class="flex w-full justify-center border-b border-gray-200 pt-1 pb-2">
            <PriceTypeFilter />
          </div>
        </div>
        <!-- Mobile Category Filter -->
        <Disclosure v-slot="{ open }" as="div" class="pb-1">
          <div class="border-b border-gray-200 pb-1">
            <DisclosureButton class="focus:outline-ph-green-600 flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-base/7 font-medium focus:outline-2 focus:outline-offset-0">
              <div class="flex items-center">
                <FontAwesomeIcon :icon="faHouseBlank" class="mr-2 size-6!" size="2xl" />
                <span class="text-gray-700">{{ t('views.search.categories') }}</span>
              </div>
              <span class="ml-6 flex items-center">
                <ChevronDownIcon :class="['h-5 w-5 transform text-gray-400', open ? '-rotate-180' : 'rotate-0']" aria-hidden="true" />
              </span>
            </DisclosureButton>
          </div>
          <DisclosurePanel class="border-b border-gray-200 pt-4 pb-8">
            <CategoryFilter />
          </DisclosurePanel>
        </Disclosure>
        <!-- Mobile Price Filter -->
        <Disclosure v-slot="{ open }" as="div" class="pb-1">
          <div class="border-b border-gray-200 pb-1">
            <DisclosureButton class="focus:outline-ph-green-600 flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-base/7 font-medium focus:outline-2 focus:outline-offset-0">
              <div class="flex items-center">
                <FontAwesomeIcon :icon="faCircleDollar" class="mr-2 size-6!" size="2xl" />
                <span class="text-gray-700">{{ t('views.search.price') }}</span>
              </div>
              <span class="ml-6 flex items-center">
                <ChevronDownIcon :class="['h-5 w-5 transform text-gray-400', open ? '-rotate-180' : 'rotate-0']" aria-hidden="true" />
              </span>
            </DisclosureButton>
          </div>
          <DisclosurePanel class="border-b border-gray-200 py-4">
            <div class="mb-4 grid justify-items-center space-y-4 divide-y divide-gray-200">
              <!-- Mobile Price Range Filter -->
              <PriceFilter />
            </div>
          </DisclosurePanel>
        </Disclosure>
        <!-- Mobile Other Filter -->
        <Disclosure v-slot="{ open }" as="div" class="pb-1">
          <div class="border-b border-gray-200 pb-1">
            <DisclosureButton class="focus:outline-ph-green-600 flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 text-base/7 font-medium focus:outline-2 focus:outline-offset-0">
              <div class="flex items-center">
                <AdjustmentsHorizontalIcon class="mr-2 size-6" />
                <span class="text-gray-700">{{ t('views.search.filters') }}</span>
              </div>
              <span class="ml-6 flex items-center">
                <ChevronDownIcon :class="['h-5 w-5 transform text-gray-400', open ? '-rotate-180' : 'rotate-0']" aria-hidden="true" />
              </span>
            </DisclosureButton>
          </div>
          <DisclosurePanel class="border-b border-gray-200 py-4">
            <div class="mb-4 grid justify-items-center space-y-4 divide-y divide-gray-200">
              <BedroomsFilter class="pb-4" />
              <BathroomsFilter />
            </div>
          </DisclosurePanel>
        </Disclosure>
        <!-- Mobile Language Switcher -->
        <div class="pt-1 pl-1.5">
          <LanguageSwitcher class="2xs:hidden flex" />
        </div>
      </MobileFilters>
    </DesktopFilters>
  </div>
</template>

<style lang="css" scoped></style>
